import { useMemo, useRef, useState } from 'react';
import { signedUsd, usd } from '../../../utilities/format';
import { t } from '../../../i18n/i18n';
import './AnalysisBarChart.scss';

/**
 * Clean SVG-based bar chart supporting positive and negative values,
 * hover tooltips, and customizable colour palettes.
 *
 * @param {{
 *   data: { label: string, value: number, note?: string }[],
 *   height?: number,
 *   colorScheme?: 'pnl' | 'blue' | 'loss' | 'accent',
 *   tooltipFormatter?: (d: { label: string, value: number }) => string,
 *   emptyMessage?: string,
 *   className?: string,
 * }} props
 */
export default function AnalysisBarChart({
  data = [],
  height = 240,
  colorScheme = 'pnl',
  tooltipFormatter,
  emptyMessage = 'No chart data available',
  className = '',
}) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const containerRef = useRef(null);

  const n = data.length;

  const { maxVal, minVal, hasNegative } = useMemo(() => {
    if (!n) return { maxVal: 1, minVal: 0, hasNegative: false };
    const vals = data.map((d) => Number(d.value) || 0);
    const max = Math.max(...vals, 0);
    const min = Math.min(...vals, 0);
    return {
      maxVal: max > 0 ? max * 1.15 : 1,
      minVal: min < 0 ? min * 1.15 : 0,
      hasNegative: min < 0,
    };
  }, [data, n]);

  if (!n) {
    return (
      <div className={`am-analysis-chart am-analysis-chart--empty ${className}`}>
        <p>{t(emptyMessage)}</p>
      </div>
    );
  }

  const range = maxVal - minVal;
  const padTop = 18;
  const padBottom = 28;
  const padLeft = 44;
  const padRight = 14;
  const svgHeight = height;
  const plotHeight = Math.max(svgHeight - padTop - padBottom, 10);

  // Y coordinate formula
  const yAt = (val) => padTop + (1 - (val - minVal) / range) * plotHeight;
  const zeroY = yAt(0);

  const formatDefault = (d) => {
    if (colorScheme === 'pnl') return `${t(d.label)}: ${signedUsd(d.value)}`;
    return `${t(d.label)}: ${usd(d.value)}`;
  };

  const getBarColor = (val) => {
    if (colorScheme === 'blue') return 'var(--btn-primary-bg, #004bff)';
    if (colorScheme === 'loss') return 'var(--capital-down, #fd4754)';
    if (colorScheme === 'accent') return 'var(--grad-tabs, #0d56eb)';
    return val >= 0 ? 'var(--capital-up, #22c55e)' : 'var(--capital-down, #fd4754)';
  };

  // 4 Y-ticks
  const yTicks = [
    maxVal,
    hasNegative ? 0 : maxVal / 2,
    hasNegative ? minVal : 0,
  ];

  return (
    <div
      ref={containerRef}
      className={`am-analysis-chart ${className}`}
      style={{ minHeight: height }}
    >
      <svg
        className="am-analysis-chart__svg"
        width="100%"
        height={svgHeight}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="barGradUp" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="barGradDown" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fd4754" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#fd4754" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="barGradBlue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#004bff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#004bff" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Gridlines */}
        {yTicks.map((tickVal, i) => {
          const y = yAt(tickVal);
          return (
            <g key={i} className="am-analysis-chart__gridline">
              <line
                x1={padLeft}
                y1={y}
                x2="100%"
                y2={y}
                stroke={tickVal === 0 ? 'var(--hairline, rgba(255,255,255,0.15))' : 'var(--hairline, rgba(255,255,255,0.06))'}
                strokeDasharray={tickVal === 0 ? undefined : '3 3'}
              />
              <text
                x={padLeft - 6}
                y={y + 3}
                textAnchor="end"
                className="am-analysis-chart__ytick"
              >
                {Math.abs(tickVal) >= 1000
                  ? `$${(tickVal / 1000).toFixed(1)}k`
                  : `$${Math.round(tickVal)}`}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const val = Number(d.value) || 0;
          const slotW = (100 - (padLeft / 4)) / n; // approximate percentage
          const pctX = (i / n) * 90 + 7;
          const y = val >= 0 ? yAt(val) : zeroY;
          const barH = Math.max(Math.abs(yAt(val) - zeroY), 2);
          const isHovered = hoveredIdx === i;

          return (
            <g
              key={d.label + i}
              className={`am-analysis-chart__bar-group${isHovered ? ' is-hovered' : ''}`}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Invisible wide hit area for easy hover */}
              <rect
                x={`${(i / n) * 88 + 8}%`}
                y={padTop}
                width={`${88 / n}%`}
                height={plotHeight}
                fill="transparent"
                style={{ cursor: 'pointer' }}
              />

              {/* The bar */}
              <rect
                className="am-analysis-chart__bar"
                x={`${(i / n) * 88 + 8 + (88 / n) * 0.15}%`}
                y={y}
                width={`${(88 / n) * 0.7}%`}
                height={barH}
                rx={3}
                fill={getBarColor(val)}
                opacity={isHovered ? 1 : 0.85}
              />

              {/* X label */}
              <text
                className="am-analysis-chart__xtick"
                x={`${(i / n) * 88 + 8 + (88 / n) * 0.5}%`}
                y={svgHeight - 8}
                textAnchor="middle"
              >
                {t(d.label)}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Floating tooltip */}
      {hoveredIdx !== null && data[hoveredIdx] && (
        <div
          className="am-analysis-chart__tooltip"
          style={{
            left: `${((hoveredIdx + 0.5) / n) * 88 + 6}%`,
            top: yAt(data[hoveredIdx].value) - 10,
          }}
        >
          {tooltipFormatter
            ? tooltipFormatter(data[hoveredIdx])
            : formatDefault(data[hoveredIdx])}
        </div>
      )}
    </div>
  );
}
